import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Alert, Button } from "antd";
import { useEffect, useRef } from "react";
import { useAgents } from "@/common/artemis-operations/agents";
import { useIsWorkspaceAdmin } from "@/common/artemis-operations/members";
import { useIsOrgAdmin } from "@/common/artemis-operations/organization";
import {
	useDeleteWorkspace,
	useUpdateWorkspace,
	useWorkspace,
} from "@/common/artemis-operations/workspaces";
import {
	DELETE_RESOURCE_DESC,
	DELETE_RESOURCE_ERROR_FAILED,
	DELETE_RESOURCE_INFO_COMPLETED,
	DELETE_RESOURCE_TITLE,
	UPDATE_RESOURCE_ERROR_FAILED,
	UPDATE_RESOURCE_INFO_COMPLETED,
} from "@/common/messages";
import { useNotify } from "@/common/use-notify";
import {
	BaseContainer,
	BasePageContainer,
	QueryGuard,
} from "@/components/base";
import { useFrictionConfirm } from "@/components/friction-confirm-dialog";
import { WorkspaceWebhooksSection } from "@/components/webhooks/workspace-webhooks-section";
import { WorkspaceAppsSection } from "@/components/workspaces/workspace-apps-section";
import { WorkspaceDetails } from "@/components/workspaces/workspace-details";
import { WorkspaceMembers } from "@/components/workspaces/workspace-members";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/settings/",
)({
	component: WorkspaceSettings,
});

function WorkspaceSettings() {
	const navigate = useNavigate();
	const { workspaceId } = Route.useParams();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const notify = useNotify();

	const { data: agents } = useAgents(workspaceId);
	const { data: workspace, isPending, isError } = useWorkspace(workspaceId);
	const updateWorkspace = useUpdateWorkspace(workspaceId);
	const deleteWorkspace = useDeleteWorkspace(workspaceId);
	const { isAdmin: isOrgAdmin } = useIsOrgAdmin();
	// Apps and webhooks are workspace governance; the API enforces the same rule.
	const isAdmin = useIsWorkspaceAdmin(workspaceId) || isOrgAdmin;

	const { confirm, dialog } = useFrictionConfirm();
	const confirmDeleteRef = useRef(() => {});
	confirmDeleteRef.current = () => {
		confirm({
			title: DELETE_RESOURCE_TITLE,
			message: DELETE_RESOURCE_DESC,
			onConfirm: () => {
				deleteWorkspace(undefined, {
					onSuccess: () => {
						notify.success(DELETE_RESOURCE_INFO_COMPLETED);
						navigate({ to: "/home" });
					},
					onError: () => notify.error(DELETE_RESOURCE_ERROR_FAILED),
				});
			},
		});
	};

	useEffect(() => {
		setTitle("Workspace settings");
		setActions([]);
		return () => setActions([]);
	}, [setTitle, setActions]);

	return (
		<QueryGuard isPending={isPending} isError={isError} isNull={!workspace}>
			<BasePageContainer>
				<BaseContainer title="General">
					<WorkspaceDetails
						workspace={workspace}
						onSaveName={(name) =>
							updateWorkspace(
								{ name },
								{
									onSuccess: () =>
										notify.success(UPDATE_RESOURCE_INFO_COMPLETED),
									onError: () => notify.error(UPDATE_RESOURCE_ERROR_FAILED),
								},
							)
						}
					/>
				</BaseContainer>

				<WorkspaceMembers workspaceId={workspaceId} />

				{isAdmin && workspace && (
					<WorkspaceAppsSection workspaceId={workspaceId} />
				)}

				{isAdmin && workspace && (
					<WorkspaceWebhooksSection workspaceId={workspaceId} />
				)}

				<BaseContainer title="Danger Zone">
					{(agents?.length || 0) > 0 && (
						<Alert
							type="warning"
							title="Workspace is not empty"
							description={
								"Cannot delete workspace with agents. Remove all agents before deleting."
							}
						/>
					)}
					<Alert
						type="error"
						title="Permanently Delete Workspace"
						action={
							<Button
								danger
								onClick={confirmDeleteRef.current}
								disabled={(agents?.length || 0) > 0}
							>
								Delete
							</Button>
						}
						description={
							"Permanently delete this workspace. This cannot be undone."
						}
					/>
				</BaseContainer>
				{dialog}
			</BasePageContainer>
		</QueryGuard>
	);
}
